import React, { useMemo } from "react"
import { useFormContext, useWatch } from "react-hook-form"
import { useTranslation } from "react-i18next"

import classNames from "classnames"

import { useEvent } from "shared/lib/useEvent"
import { IconSize } from "shared/types/Icon"
import { SelectOption } from "shared/types/Select"
import { WithClassName } from "shared/types/WithClassName"
import { ChipList } from "shared/ui/chip-list/ChipList"
import { Icon } from "shared/ui/icon/Icon"
import { IconName } from "shared/ui/icon/__generated__/IconName"
import { Combobox } from "shared/ui/input/combobox/Combobox"
import { MediaOption } from "shared/ui/media-option/MediaOption"

import { FloorFormData } from "features/floor-management/types/FloorFormData"

import { useFloorFormContext } from "../../contexts/FloorFormContext"

export type FloorRoomAssignmentInputProps = WithClassName

export const FloorRoomAssignmentInput = React.memo(
  ({ className }: FloorRoomAssignmentInputProps) => {
    const { t } = useTranslation()
    const { isReadonly, isDisabled, roomOptions, isLoadingRoomOptions } =
      useFloorFormContext()

    const { setValue } = useFormContext<FloorFormData>()
    const roomIDs = useWatch<FloorFormData, "roomIDs">({
      name: "roomIDs"
    })

    const selectedRooms = useMemo(() => {
      if (!roomIDs || roomIDs.length === 0 || !roomOptions) {
        return []
      }
      return roomIDs
        .map(id => roomOptions.find(option => option.value === id))
        .filter(Boolean) as SelectOption<string>[]
    }, [roomIDs, roomOptions])

    const handleRemoveRoom = useEvent((roomID: string) => {
      const currentRoomIDs = roomIDs || []
      const updatedRoomIDs = currentRoomIDs.filter(id => id !== roomID)
      setValue("roomIDs", updatedRoomIDs.length > 0 ? updatedRoomIDs : null, {
        shouldDirty: true,
        shouldValidate: true
      })
    })

    if (isReadonly) {
      return null
    }

    return (
      <div className={classNames("floor-form__room-assignment", className)}>
        <Combobox.Root
          name="roomIDs"
          label={t("Floor.RoomAssignment")}
          loading={isLoadingRoomOptions}
          disabled={isDisabled}
          readonly={isReadonly}
          className="floor-form__room-assignment-combobox"
          items={roomOptions || []}
          multiple
        >
          <Combobox.Label />
          <Combobox.Control>
            <Combobox.Trigger>
              <Combobox.Value>
                {() => (
                  <span className="combobox__placeholder">
                    {t("Floor.RoomAssignmentPlaceholder")}
                  </span>
                )}
              </Combobox.Value>
              <Combobox.Icon />
            </Combobox.Trigger>
            <Combobox.Portal>
              <Combobox.Positioner align="start" sideOffset={8}>
                <Combobox.Popup aria-label={t("Floor.RoomAssignment")}>
                  <Combobox.InputContainer>
                    <Combobox.Input />
                    <Icon name={IconName.SEARCH} />
                  </Combobox.InputContainer>
                  <Combobox.Empty />
                  <Combobox.List>
                    {(option: SelectOption<string>) => (
                      <Combobox.Item key={option.value} value={option}>
                        <Combobox.ItemIndicator />
                        <Combobox.ItemContent>
                          {option.label}
                        </Combobox.ItemContent>
                      </Combobox.Item>
                    )}
                  </Combobox.List>
                </Combobox.Popup>
              </Combobox.Positioner>
            </Combobox.Portal>
          </Combobox.Control>
          <Combobox.ErrorMessage />
        </Combobox.Root>

        {selectedRooms.length > 0 && (
          <ChipList.List
            aria-label={t("Floor.AssignedRooms")}
            className="floor-form__room-chips"
          >
            {selectedRooms.map(room => (
              <ChipList.Chip
                key={room.value}
                as="button"
                onClick={() => handleRemoveRoom(room.value)}
                className="floor-form__room-chip"
              >
                <MediaOption.Root className="floor-form__room-chip-label">
                  <MediaOption.Media>
                    <Icon name={IconName.ROOM} size={IconSize.SMALL} />
                  </MediaOption.Media>
                  <MediaOption.Label>{room.label}</MediaOption.Label>
                </MediaOption.Root>
                <Icon name={IconName.CLOSE} size={IconSize.XS} />
              </ChipList.Chip>
            ))}
          </ChipList.List>
        )}
      </div>
    )
  }
)
